import { isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, OnDestroy, PLATFORM_ID } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { fromEvent, merge, Subscription, throttleTime } from 'rxjs';
import { AuthService } from '../../auth/services/auth.service';
import { CustomToastComponent } from '../../shared/components/toast/custom-toast.component';
import { HttpService } from '../../shared/services/http.service';

@Injectable({
    providedIn: 'root'
})
export class IdleTimeoutService implements OnDestroy {
    private idleSub!: Subscription;
    private checkInterval!: any;
    private apiCallInterval!: any;
    private readonly TOTAL_IDLE_TIME = 15 * 60 * 1000; // 15 minutes
    private readonly WARNING_TIME = 10 * 60 * 1000; // 10 minutes
    private readonly CHECK_INTERVAL = 10 * 60 * 1000;  // 10 minutes
    private readonly API_CALL_INTERVAL = 10 * 60 * 1000;  // 10 minutes
    private alertShown = false;
    private isRunning = false;
    private lastActivityTime = Date.now();
    private lastApiCallTime = 0;
    private isUserCurrentlyActive = false;

    constructor(
        private authService: AuthService,
        private snackBar: MatSnackBar,
        private httpService: HttpService,
        @Inject(PLATFORM_ID) private platformId: any
    ) { }

    startWatching(): void {
        if (!isPlatformBrowser(this.platformId) || this.isRunning) {
            return;
        }
        this.isRunning = true;
        this.lastActivityTime = Date.now();
        this.lastApiCallTime = Date.now();
        this.alertShown = false;
        this.isUserCurrentlyActive = true;

        const events = [
            'mousemove', 'mousedown', 'click', 'scroll',
            'keypress', 'keydown', 'keyup', 'touchstart',
            'touchend', 'touchmove', 'wheel', 'resize',
            'focus', 'blur', 'visibilitychange'
        ];

        const activityEvents = events.map(event =>
            fromEvent(document, event).pipe(throttleTime(1000))
        );

        const activity$ = merge(...activityEvents);

        this.idleSub = activity$.subscribe(() => {
            this.handleUserActivity();
        });

        this.checkInterval = setInterval(() => {
            this.checkIdleTime();
        }, this.CHECK_INTERVAL);
        this.apiCallInterval = setInterval(() => {
            this.checkApiCall();
        }, this.CHECK_INTERVAL);
    }

    private handleUserActivity(): void {
        if (!this.isRunning) return;
        const wasInactive = !this.isUserCurrentlyActive;
        this.lastActivityTime = Date.now();
        this.isUserCurrentlyActive = true;

        if (this.alertShown) {
            this.alertShown = false;
            // this.showSuccessToast('Activity detected! Timer reset.');
        }

        if (wasInactive) {
            this.lastApiCallTime = Date.now();
        }
        this.checkApiCall();
    }

    private checkApiCall(): void {
        if (!this.isRunning || !this.isUserCurrentlyActive) return;
        const now = Date.now();
        const timeSinceLastApiCall = now - this.lastApiCallTime;
        if (timeSinceLastApiCall >= this.API_CALL_INTERVAL) {
            this.makeKeepAliveApiCall();
        }
    }

    private checkIdleTime(): void {
        if (!this.isRunning) return;
        const now = Date.now();
        const idleTime = now - this.lastActivityTime;
        this.isUserCurrentlyActive = idleTime < (2 * 60 * 1000);
        if (idleTime >= this.WARNING_TIME && idleTime < this.TOTAL_IDLE_TIME && !this.alertShown) {
            this.showWarning();
        }
        if (idleTime >= this.TOTAL_IDLE_TIME) {
            this.handleLogout();
        }
    }

    private makeKeepAliveApiCall(): void {
        if (!isPlatformBrowser(this.platformId) || !this.isUserCurrentlyActive) return;

        const apiUrl = 'session/keep-alive';

        this.httpService.get(apiUrl).subscribe({
            next: (response) => {
                this.lastApiCallTime = Date.now();
            },
            error: (error) => {
                console.error('Keep-alive API call failed', error);
                this.lastApiCallTime = Date.now();
            }
        });
    }

    private showSuccessToast(message: string): void {
        if (!isPlatformBrowser(this.platformId)) return;

        this.snackBar.openFromComponent(CustomToastComponent, {
            data: { message },
            duration: 3000,
            horizontalPosition: 'right',
            verticalPosition: 'top',
            panelClass: ['no-padding-snackbar', 'success-toast']
        });
    }

    private showErrorToast(message: string | null | undefined): void {
        if (!message) return;
        if (!isPlatformBrowser(this.platformId)) return;

        this.snackBar.openFromComponent(CustomToastComponent, {
            data: { message },
            duration: 3000,
            horizontalPosition: 'right',
            verticalPosition: 'top',
            panelClass: ['no-padding-snackbar', 'error-toast']
        });
    }

    stopWatching(): void {
        this.isRunning = false;
        this.alertShown = false;
        this.isUserCurrentlyActive = false;
        this.idleSub?.unsubscribe();

        if (this.checkInterval) {
            clearInterval(this.checkInterval);
        }

        if (this.apiCallInterval) {
            clearInterval(this.apiCallInterval);
        }
    }

    resetIdleTimer(): void {
        if (this.isRunning) {
            this.lastActivityTime = Date.now();
            this.isUserCurrentlyActive = true;
            this.alertShown = false;
        }
    }

    forceApiCall(): void {
        if (this.isUserCurrentlyActive) {
            this.makeKeepAliveApiCall();
        }
    }

    private showWarning(): void {
        this.alertShown = true;
        const remainingTime = Math.round((this.TOTAL_IDLE_TIME - this.WARNING_TIME) / 1000);
        this.showErrorToast(`You will be logged out in ${remainingTime} seconds due to inactivity.`);
    }

    private handleLogout(): void {
        this.showErrorToast('You have been logged out due to inactivity.');
        this.authService.logout();
        this.stopWatching();
    }

    ngOnDestroy(): void {
        this.stopWatching();
    }
}